import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '@homeleite/arch';
import { finalize, timeout } from 'rxjs/operators';

interface InboundItem {
    _id: string;
    tenantId: string | null;
    entity: string;
    method: string;
    pathId?: string | null;
    attempts: number;
    association?: string | null;
    receivedAt: string | null;
    status: string;
    detail?: string | null;
    processedAt?: string | null;
    durationMs?: number | null;
    retryable?: boolean | null;
    truncated?: boolean;
    body?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
    RECEIVED: 'Recebido',
    PROCESSED: 'Processado',
    FAILED: 'Com erro',
    IGNORED: 'Ignorado',
    PENDING_DEPENDENCY: 'Aguardando dependência',
    PENDING_TENANT: 'Sem associação'
};

@Component({
    selector: 'app-inbound-panel',
    templateUrl: './inbound-panel.component.html',
    styleUrls: ['./inbound-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InboundPanelComponent implements OnInit {
    @Input() tenants: string[] = [];

    state: 'loading' | 'error' | 'ready' = 'loading';
    items: InboundItem[] = [];
    tenantId = '';
    entity = '';
    status = '';
    days = '7';
    limit = 50;
    busy = new Set<string>();
    openId: string | null = null;
    detail: InboundItem | null = null;
    detailState: 'idle' | 'loading' | 'error' = 'idle';
    readonly statuses = Object.keys(STATUS_LABELS);

    constructor(private api: ApiService, private cd: ChangeDetectorRef, private snackBar: MatSnackBar) {}

    ngOnInit(): void {
        this.load();
    }

    statusLabel(s: string): string {
        return STATUS_LABELS[s] || s;
    }

    load(): void {
        this.state = 'loading';
        this.cd.markForCheck();
        let params = new HttpParams().set('limit', this.limit);
        if (this.tenantId) params = params.set('tenantId', this.tenantId);
        if (this.entity.trim()) params = params.set('entity', this.entity.trim());
        if (this.status) params = params.set('status', this.status);
        if (this.days) params = params.set('from', new Date(Date.now() - Number(this.days) * 86400000).toISOString());
        this.api.root
            .path('integration-sync', 'inbound')
            .getJson<InboundItem[]>({ params })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.items = r;
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.state = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    toggle(item: InboundItem): void {
        if (this.openId === item._id) {
            this.openId = null;
            this.detail = null;
            this.cd.markForCheck();
            return;
        }
        this.openId = item._id;
        this.detail = null;
        this.detailState = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'inbound')
            .getJson<InboundItem[]>({ params: new HttpParams().set('id', item._id).set('limit', 1) })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    if (this.openId !== item._id) return;
                    this.detail = r[0] || null;
                    this.detailState = 'idle';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.detailState = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    prettyBody(body: string | null | undefined): string {
        if (!body) return '(sem corpo)';
        try {
            return JSON.stringify(JSON.parse(body), null, 2);
        } catch {
            return body;
        }
    }

    canReprocess(item: InboundItem): boolean {
        return item.status !== 'PROCESSED' && item.retryable !== false;
    }

    reprocess(item: InboundItem): void {
        if (!confirm(`Reprocessar este evento (${item.entity})?`)) return;
        this.busy.add(item._id);
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'inbound', 'reprocess')
            .postJson<{ id: string; status: string; detail?: string }[]>({}, { params: new HttpParams().set('id', item._id) })
            .pipe(
                timeout(60000),
                finalize(() => {
                    this.busy.delete(item._id);
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: (r) => {
                    const s = r[0]?.status;
                    this.snackBar.open(s ? `Resultado: ${this.statusLabel(s)}` : 'Nada a processar', 'OK', { duration: 5000 });
                    this.load();
                },
                error: () => this.snackBar.open('Falha ao reprocessar (API indisponível ou tempo esgotado)', 'OK', { duration: 5000 })
            });
    }
}
